import {loggingInfo, params, systemInfo} from '@/systemInfo.js';
import {showNotification} from "@/services/notification.js";
import {rebootDevice} from "@/services/reboot.js";
import {getFormattedDateTime} from "@/services/helperFunctions.js";
import {getLogLevel} from "@/services/logging.js";

// Generate a unique message ID for each communication
const generateMessageId = () => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Track app restarts for system reboot prevention
const appRestarts = new Map();
const requestedInfo = {};

const communicationDebug = {
    log: (appName, message, data = null) => {
        const timestamp = new Date().toISOString();
        const logMessage = `[${timestamp}] COMM-DEBUG [${appName}]: ${message}`;
        console.log(logMessage, data || '');
    }
};

let _activeSpectatorWindow = null

/**
 * Initialize communication with a child app
 * @param {Object} app - The app object
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {Object} state - Reactive state objects
 * @param {Object} state.communicationInProgress - Ref for tracking if communication is in progress
 * @param {Object} state.communicationComplete - Ref for tracking if communication is complete
 * @param {Function} onFailed - Callback when communication fails
 */
export function initializeAppCommunication(app, iframe, state, onFailed) {
    communicationDebug.log(app.name, 'Initializing communication', {
        hasParams: !!(app.params && app.params.length > 0),
        iframeReady: !!iframe.contentWindow
    });

    if (state.communicationInProgress.value || state.communicationComplete.value) return;

    state.communicationInProgress.value = true;

    // Extract requested system info
    const requestedInfo = prepareSystemInfo(app);

    // Set up message handlers for all types of messages
    setupChildAppMessageHandlers(app, iframe.contentWindow, {
        systemInfo: requestedInfo,
        state: state,
        onFailed: (error) => {
            // Pass the error to the callback
            if (onFailed) onFailed(error);
        }
    });

    // For apps with no params, mark as complete after a short delay
    if (!app.params || app.params.length === 0) {
        setTimeout(() => {
            state.communicationComplete.value = true;
        }, 500);
    } else {
        // Progressive retry strategy for slow networks
        let retryCount = 0;
        const maxRetries = params.communicationAttemptRetries;
        const baseDelay = params.baseRetryDelay * 1000;

        const attemptCommunication = () => {
            if (state.communicationComplete.value) return;

            retryCount++;
            const delay = baseDelay * retryCount; // Progressive delay: 2s, 4s, 6s, 8s, 10s

            console.log(`[COMM] ${app.name} - Communication attempt ${retryCount}/${maxRetries} (delay: ${delay}ms)`);

            setTimeout(() => {
                if (state.communicationInProgress.value && !state.communicationComplete.value) {
                    console.log(`[COMM] ${app.name} - Sending system info (attempt ${retryCount})`);

                    try {
                        // Try multiple message formats for better compatibility
                        const messages = [
                            {
                                type: 'SYSTEM_INFO',
                                messageId: `attempt_${retryCount}_${Date.now()}`,
                                data: requestedInfo
                            },
                            {
                                type: 'SYSTEM_INFO_REQUEST_RESPONSE',
                                messageId: `fallback_${retryCount}_${Date.now()}`,
                                data: requestedInfo
                            }
                        ];

                        messages.forEach((msg, index) => {
                            setTimeout(() => {
                                iframe.contentWindow.postMessage(msg, '*');
                            }, index * 100); // Stagger messages by 100ms
                        });

                        // Schedule next retry if this one fails
                        if (retryCount < maxRetries) {
                            setTimeout(() => {
                                if (state.communicationInProgress.value && !state.communicationComplete.value) {
                                    attemptCommunication();
                                }
                            }, delay + 1000); // Wait for response before next attempt
                        }
                    } catch (error) {
                        console.warn(`[COMM] ${app.name} - Failed to send system info (attempt ${retryCount}):`, error);
                        if (retryCount < maxRetries) {
                            attemptCommunication();
                        }
                    }
                }
            }, delay);
        };

        // Start the first attempt
        attemptCommunication();
    }
}

/**
 * Handle errors from child app communication
 * @param {Object} app - The app object
 * @param {Error} error - The error object
 */
export function handleAppCommunicationError(app, error) {
    console.error(`Error communicating with ${app.name}:`, error);
    showNotification(`Failed to launch ${app.name}: ${error.message}`, '', false, true);
}

/**
 * Set up message listeners for child app commands
 * @param {Object} app - The app object
 * @param {Window} childWindow - The iframe's contentWindow
 * @param {Object} options - Options for the message handler
 * @param {Object} options.systemInfo - System info to send to the child app
 * @param {Object} options.state - Reactive state objects
 * @param {Function} options.onFailed - Callback when communication fails
 */
export function setupChildAppMessageHandlers(app, childWindow, options = {}) {
    // Clean up any existing handlers first
    cleanupChildAppMessageHandlers(app);
    _activeSpectatorWindow = childWindow?.contentWindow ? childWindow.contentWindow : childWindow

    // Extract options
    const {
        systemInfo = {},
        state = {
            communicationInProgress: {value: false},
            communicationComplete: {value: false}
        },
        onFailed = () => {
        }
    } = options;

    // Set up timeout for system info response
    let timeoutId = null;
    let communicationStarted = false;

    if (app.params && app.params.length > 0) {
        timeoutId = setTimeout(() => {
            if (!communicationStarted) {
                console.warn(`No communication attempt detected for ${app.name}`);
            }

            state.communicationInProgress.value = false;

            // Enhanced diagnostic information
            const diagnostics = {
                appName: app.name,
                appUrl: app.url || app.location,
                hasParams: !!(app.params && app.params.length > 0),
                paramsCount: app.params ? app.params.length : 0,
                iframeLoaded: !!childWindow,
                iframeAccessible: false,
                communicationStarted,
                communicationState: {
                    inProgress: state.communicationInProgress.value,
                    complete: state.communicationComplete.value
                }
            };

            // Test iframe accessibility
            try {
                const doc = childWindow.document;
                const readyState = doc.readyState;
                diagnostics.iframeAccessible = true;
                diagnostics.iframeReadyState = readyState;
                diagnostics.iframeUrl = doc.URL;
            } catch (e) {
                diagnostics.iframeAccessible = false;
                diagnostics.iframeError = e.message;
            }

            const diagnosticMsg = `Communication timeout for ${diagnostics.appName}: ${JSON.stringify(diagnostics, null, 2)}`;

            console.error(diagnosticMsg);
            const timeoutError = new Error(`Communication timeout - ${diagnostics.appName}`);
            showNotification(`Failed to launch ${app.name}: Communication timeout`, '', false, true);
            onFailed(timeoutError);
        }, params.appStartTimeout * 1000);
    }

    const messageHandler = (event) => {
        try {
            // Verify the origin for security if URL is available
            if (app.url || app.location) {
                try {
                    const appOrigin = new URL(app.url || app.location).origin;
                    if (event.origin !== appOrigin && event.origin !== 'null') {
                        console.warn(`Received message from unexpected origin: ${event.origin}, expected: ${appOrigin}`);
                        return;
                    }
                } catch (urlError) {
                    console.warn(`Invalid URL for origin check: ${app.url || app.location}`, urlError);
                }
            }

            // Handle different message types
            if (event.data && event.data.type) {
                // Mark that communication has started
                communicationStarted = true;

                switch (event.data.type) {
                    case 'SYSTEM_INFO_REQUEST':
                        console.log(`Received system info request from ${app.name}`);

                        // Send the system info to the child app
                        event.source.postMessage({
                            type: 'SYSTEM_INFO',
                            messageId: event.data.messageId,
                            data: systemInfo
                        }, event.origin);
                        break;

                    case 'SYSTEM_INFO_RESPONSE':
                        console.log(`Received system info response from ${app.name}:`, event.data);

                        // Clear the timeout
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            timeoutId = null;
                        }

                        state.communicationInProgress.value = false;

                        if (event.data.success) {
                            console.log(`Communication with ${app.name} successful`);
                            state.communicationComplete.value = true;
                        } else {
                            const responseError = new Error(event.data.message || `Failed to launch ${app.name}`);
                            console.error(`Communication with ${app.name} failed:`, responseError.message);
                            showNotification(`Failed to launch ${app.name}: ${responseError.message}`, '', false, true);
                            onFailed(responseError);
                        }
                        break;

                    case 'NOTIFICATION':
                        // Show notification from child app
                        console.log(`Received notification from ${app.name}:`, event.data.message);

                        showNotification(
                            event.data.message,
                            '',
                            event.data.isPersistent,
                            event.data.isImportant,
                            event.data.l
                        );

                        // Acknowledge receipt
                        event.source.postMessage({
                            type: 'NOTIFICATION_RESPONSE',
                            messageId: event.data.messageId,
                            success: true
                        }, event.origin);
                        break;

                    case 'APP_RESTART':
                        // Handle app restart request
                        console.log(`Received restart request from ${app.name}`);

                        restartApp(app)
                            .then(result => {
                                event.source.postMessage({
                                    type: 'APP_RESTART_RESPONSE',
                                    messageId: event.data.messageId,
                                    success: true,
                                    reboot: result.reboot || false
                                }, event.origin);
                            })
                            .catch(error => {
                                console.error(`Failed to restart app ${app.name}:`, error);
                                event.source.postMessage({
                                    type: 'APP_RESTART_RESPONSE',
                                    messageId: event.data.messageId,
                                    success: false,
                                    message: error.message
                                }, event.origin);
                            });
                        break;

                    case 'CUSTOM_MESSAGE':
                        const message = event.data.message.toLowerCase();

                        console.log(`CUSTOM_MESSAGE received from ${app.name}: ${message}`)

                        switch (message) {
                            case "spectator.validid":
                                event.source.postMessage({data: 'bgntv.init'}, event.origin);
                                event.source.postMessage({data: 'bgntv.loaded'}, event.origin);

                                window.dispatchEvent(new CustomEvent('valid-id'));

                                // Mark communication as complete to continue with app display
                                if (state.communicationComplete) {
                                    state.communicationComplete.value = true;
                                }
                                break;

                            case "spectator.invalidid":
                                window.dispatchEvent(new CustomEvent('invalid-id'));

                                // Mark communication as complete to continue with app display
                                if (state.communicationComplete) {
                                    state.communicationComplete.value = true;
                                }
                                break;

                            case "spectator.endgame":
                                console.log('Live Trivia: Game ended');
                                window.dispatchEvent(new CustomEvent('game-ended'));

                                break;
                        }
                        break;

                    case 'LOGGING_INIT_REQUEST':
                        event.source.postMessage({
                            type: 'LOGGING_INIT',
                            level: getLogLevel(),
                            r7: {
                                token: import.meta.env.VITE_R7_KEY,
                                region: import.meta.env.VITE_R7_REG,
                            },
                            siteInfo: loggingInfo.value,
                        }, event.origin)
                        break;
                }

                // Send acknowledgment for all message types
                try {
                    event.source.postMessage({
                        type: 'MESSAGE_RESPONSE',
                        data: 'BGN TV - Message Acknowledged',
                        messageId: event.data.messageId,
                        success: true
                    }, event.origin);
                } catch (ackError) {
                    console.warn(`Failed to send acknowledgment to ${app.name}:`, ackError);
                }
            }
        } catch (error) {
            console.error(`Error handling message from ${app.name}:`, error);

            // If this was during system info communication, handle failure
            if (state.communicationInProgress.value && !state.communicationComplete.value) {
                state.communicationInProgress.value = false;
                showNotification(`Error communicating with ${app.name}: ${error.message}`, '', false, true);
                onFailed(error);

                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
            }
        }
    };

    // Add the event listener
    window.addEventListener('message', messageHandler);

    // Store the handler reference for cleanup
    app.messageHandler = messageHandler;
    app.timeoutId = timeoutId; // Store timeout ID for cleanup

    return messageHandler;
}

/**
 * Set leg level of active spectator app
 * @param {String} level - log level to set
 */
export function setSpectatorLogLevel(level) {
    if (!_activeSpectatorWindow) return
    try {
        _activeSpectatorWindow.postMessage({
            type: 'LOG_LEVEL',
            level
        }, '*')
    } catch (e) {
        console.warn('[COMM] Failed to update spectator log level', e)
    }
}

/**
 * Remove message handlers when app is closed
 * @param {Object} app - The app object
 */
export function cleanupChildAppMessageHandlers(app) {
    _activeSpectatorWindow = null
    if (app.messageHandler) {
        console.log(`Removing message handler for ${app.name}`);
        window.removeEventListener('message', app.messageHandler);
        app.messageHandler = null;
    }

    // Also clean up any pending timeouts
    if (app.timeoutId) {
        clearTimeout(app.timeoutId);
        app.timeoutId = null;
    }
}

/**
 * Check if an app has been restarted too many times in a short period
 * @param {string} appId - The app identifier
 * @returns {boolean} - True if the app should trigger a system reboot
 */
function shouldRebootSystem(appId) {
    if (!appRestarts.has(appId)) {
        appRestarts.set(appId, []);
    }

    const restarts = appRestarts.get(appId);
    const now = Date.now();

    // Add current restart
    restarts.push(now);

    // Filter out restarts older than 10 minutes
    const tenMinutesAgo = now - 10 * 60 * 1000;
    const recentRestarts = restarts.filter(time => time > tenMinutesAgo);

    // Update the map with only recent restarts
    appRestarts.set(appId, recentRestarts);

    // If 3 or more restarts in the last 10 minutes, trigger reboot
    return recentRestarts.length >= 3;
}

/**
 * Restart a child app
 * @param {Object} app - The app object
 * @returns {Promise} - Resolves when app is restarted
 */
export function restartApp(app) {
    return new Promise((resolve, reject) => {
        try {
            // Check if we should reboot the system
            if (shouldRebootSystem(app.id)) {
                showNotification(`${app.name} has restarted multiple times. System will reboot.`, '', true, true);
                console.warn(`${app.name} has been restarted 3+ times in 10 minutes. Rebooting system...`);

                // Add reboot info to systemInfo for tracking
                systemInfo.lastRebootReason = `Multiple restarts of ${app.name}`;
                systemInfo.lastRebootTime = new Date().toISOString();

                // Implement system reboot - this would depend on your platform
                setTimeout(() => {
                    console.log("SYSTEM REBOOT TRIGGERED");
                    rebootDevice(`${app.name} multiple restarts`)
                }, 3000);

                resolve({reboot: true});
            } else {
                // Close and relaunch the app
                showNotification(`Restarting ${app.name}...`, '', true);

                // Update systemInfo with restart information
                systemInfo.lastAppRestart = {
                    appId: app.id,
                    appName: app.name,
                    time: getFormattedDateTime()
                };

                // Get references to the app viewer component and app tile
                // We need to use a custom event since we can't directly access the component
                const restartEvent = new CustomEvent('app-restart-requested', {
                    detail: {
                        appId: app.id,
                        appName: app.name
                    }
                });

                // Dispatch the event to be handled by the main app component
                window.dispatchEvent(restartEvent);

                // Return success - the actual restart will be handled by the event listener
                resolve({
                    restarted: true,
                    message: "App restart initiated"
                });
            }
        } catch (error) {
            reject(error);
        }
    });
}


/**
 * Prepare system information based on app parameters
 * @param {Object} app - The app object
 * @returns {Object} - The prepared system info
 */
function prepareSystemInfo(app) {
    // const requestedInfo = {};

    if (app.params && app.params.length > 0) {
        console.log("PARAMS", app.params);

        app.params.forEach(param => {
            console.log(`Accessing systemInfo[${param}]:`, systemInfo[param]);

            let value;
            let keyName = param.includes('.') ? param.split('.')[0] : param; // Extract initial word as key name

            if (param.includes('.')) {
                // Resolve nested objects
                value = getNestedObject(systemInfo, param);
                console.log(`Resolved nested value for ${param}:`, value);
            } else {
                // Directly access simple keys
                value = systemInfo[param];
            }

            if (value !== undefined) {
                // Use the initial word as the key name for requestedInfo
                requestedInfo[keyName] = makeSerializable(value);
            } else {
                console.warn(`Value for ${param} is undefined`);
            }
        });
    }

    if (systemInfo.serialNumber !== undefined) {
        requestedInfo.serialNumber = makeSerializable(systemInfo.serialNumber);
    } else {
        console.warn('[COMM] serialNumber is not available in systemInfo');
    }

    console.log('System info ready...', requestedInfo)

    return requestedInfo;
}

/**
 * Make objects serializable by removing proxies and handling special types
 * @param {any} obj - The object to make serializable
 * @returns {any} - A serializable version of the object
 */
export function makeSerializable(obj) {
    if (!obj) return obj;

    // Handle primitive values
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
        return obj.map(item => makeSerializable(item));
    }

    // Handle Date objects
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }

    // Handle regular objects
    const result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            // Skip functions and symbols which can't be serialized
            if (typeof obj[key] !== 'function' && typeof obj[key] !== 'symbol') {
                result[key] = makeSerializable(obj[key]);
            }
        }
    }

    return result;
}

/**
 * Get a nested property from an object using dot notation
 * @param {Object} obj - The object to get the property from
 * @param {string} path - The path to the property using dot notation
 * @returns {any} - The value at the specified path or undefined
 */
export function getNestedObject(obj, path) {
    console.log(`getting nested value for ${path} from ${obj}`);
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
}